import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import { useMe } from "@/api/auth";
import { useBalance } from "@/api/balance";
import {
  useDepositMemo,
  useGenerateDepositMemo,
  usePaymentDestinations,
  useUpdateDepositAddress,
} from "@/api/deposit";
import { useTotpStatus } from "@/api/totp";
import { openTotpPrompt } from "@/components/TotpPrompt";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

type Method = "bybit" | "ton";

export function Balance() {
  const balance = useBalance();
  const me = useMe();
  const dest = usePaymentDestinations();
  const t = useT();

  // Default tab: TON when configured AND already has memo, else Bybit.
  // Falls back to Bybit on the very first render before `dest` resolves
  // so the user always sees content.
  const [tab, setTab] = useState<Method>("bybit");
  useEffect(() => {
    if (!dest.data) return;
    if (dest.data.ton.ready) {
      setTab((prev) => (prev === "ton" ? "ton" : "bybit"));
    }
  }, [dest.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("balance.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("balance.subtitle")}</p>
      </div>

      {/* Available balance - single big number at the top. */}
      <section className="card flex flex-wrap items-baseline justify-between gap-2 p-4 sm:p-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {t("balance.available")}
          </div>
          <div className="mt-2 text-3xl font-semibold text-slate-800 sm:text-4xl">
            ${formatBalance(balance.data?.balance, me.data?.balance)}
          </div>
        </div>
        <div className="text-xs text-slate-400">{t("balance.live")}</div>
      </section>

      {/* Method picker. Two equal-weight buttons; the active one gets
          the accent color so the page never looks "empty" while the
          user picks. */}
      <div className="flex gap-2 rounded-md bg-slate-100 p-1 sm:max-w-md">
        <TabButton
          active={tab === "bybit"}
          onClick={() => setTab("bybit")}
          label={t("balance.tab.bybit")}
          sublabel={t("balance.tab.bybit.sub")}
        />
        <TabButton
          active={tab === "ton"}
          onClick={() => setTab("ton")}
          label={t("balance.tab.ton")}
          sublabel={t("balance.tab.ton.sub")}
        />
      </div>

      {tab === "bybit" ? (
        <BybitMethod
          ready={!!dest.data?.bybit.ready}
          operatorUid={dest.data?.bybit.operator_uid ?? null}
        />
      ) : (
        <TonMethod
          ready={!!dest.data?.ton.ready}
          walletAddress={dest.data?.ton.wallet_address ?? null}
          memo={dest.data?.ton.memo ?? null}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
  sublabel,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  sublabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex-1 rounded px-3 py-2 text-left transition " +
        (active
          ? "bg-white shadow-sm"
          : "text-slate-500 hover:bg-white/60")
      }
    >
      <div
        className={
          "text-sm font-semibold " +
          (active ? "text-accent" : "text-slate-700")
        }
      >
        {label}
      </div>
      <div className="text-xs text-slate-500">{sublabel}</div>
    </button>
  );
}

/* -------------------------- Bybit method ---------------------------- */

function BybitMethod({
  ready,
  operatorUid,
}: {
  ready: boolean;
  operatorUid: string | null;
}) {
  const t = useT();
  return (
    <section className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-slate-800">
        {t("balance.bybit.title")}
      </h2>
      <p className="mt-1 text-sm text-slate-500">{t("balance.bybit.intro")}</p>

      <Step n={1} title={t("balance.bybit.step1")}>
        <p className="text-sm text-slate-600">{t("balance.bybit.step1.desc")}</p>
        <SenderUidForm />
      </Step>

      <Step n={2} title={t("balance.bybit.step2")}>
        {ready && operatorUid ? (
          <CopyCard
            label={t("balance.bybit.copy_label")}
            value={operatorUid}
            note={t("balance.bybit.copy_note")}
          />
        ) : (
          <Notice>{t("balance.bybit.no_uid_notice")}</Notice>
        )}
      </Step>
    </section>
  );
}

function SenderUidForm() {
  const me = useMe();
  const totp = useTotpStatus();
  const update = useUpdateDepositAddress();
  const push = useToasts((s) => s.push);
  const t = useT();

  const stored = String(
    (me.data?.deposit_address as { bybit?: unknown } | null)?.bybit ?? "",
  );
  const displayed = stored && stored !== "0" ? stored : "";

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [pwd, setPwd] = useState("");

  const startEdit = () => {
    setValue(displayed);
    setPwd("");
    setEditing(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      push({ kind: "error", title: t("balance.bybit.uid_required") });
      return;
    }

    const askCode =
      !!totp.data?.enabled && !!totp.data?.require_deposit_change;
    let codeError: string | undefined;
    for (;;) {
      let code: string | undefined;
      if (askCode) {
        const got = await openTotpPrompt({
          title: t("balance.bybit.confirm.title"),
          description: t("balance.bybit.confirm.desc"),
          errorMessage: codeError,
        });
        if (got === null) return;
        code = got;
      }
      try {
        await update.mutateAsync({
          bybit: trimmed,
          current_password: pwd,
          ...(code ? { code } : {}),
        });
        push({ kind: "success", title: t("balance.bybit.uid_saved") });
        setEditing(false);
        setPwd("");
        return;
      } catch (err) {
        const e = err as { status?: unknown; detail?: unknown };
        if (
          e.status === 428 &&
          typeof e.detail === "object" &&
          e.detail !== null &&
          (e.detail as { requires_totp?: unknown }).requires_totp === true
        ) {
          codeError = t("balance.wrong_code");
          continue;
        }
        const msg =
          typeof e.detail === "string"
            ? e.detail
            : t("balance.bybit.save_failed.body");
        push({ kind: "error", title: t("balance.bybit.save_failed"), body: msg });
        return;
      }
    }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-surface-border bg-slate-50 px-3 py-2">
        <div className="font-mono text-sm text-slate-800">
          {displayed || (
            <span className="text-slate-400">{t("balance.bybit.not_set")}</span>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={startEdit}
          >
            {displayed ? t("balance.bybit.change") : t("balance.bybit.set_uid")}
          </button>
        )}
      </div>

      {editing && (
        <form
          onSubmit={submit}
          className="mt-3 grid gap-3 sm:max-w-md"
        >
          <div>
            <label className="label" htmlFor="bybit_uid">
              {t("balance.bybit.uid_label")}
            </label>
            <input
              id="bybit_uid"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className="input font-mono"
              placeholder={t("balance.bybit.uid_placeholder")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="bybit_pwd">
              {t("balance.password.current")}
            </label>
            <input
              id="bybit_pwd"
              type="password"
              autoComplete="current-password"
              className="input"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => {
                setEditing(false);
                setPwd("");
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary text-xs"
              disabled={update.isPending || !value.trim() || !pwd}
            >
              {update.isPending ? t("balance.saving") : t("common.save")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* --------------------------- TON method ----------------------------- */

function TonMethod({
  ready,
  walletAddress,
  memo,
}: {
  ready: boolean;
  walletAddress: string | null;
  memo: string | null;
}) {
  const t = useT();
  if (!ready || !walletAddress) {
    return (
      <section className="card p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-slate-800">{t("balance.ton.title")}</h2>
        <Notice variant="warn">{t("balance.ton.unconfigured")}</Notice>
      </section>
    );
  }

  return (
    <section className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-slate-800">{t("balance.ton.title")}</h2>
      <p className="mt-1 text-sm text-slate-500">{t("balance.ton.intro")}</p>

      <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800">
        {t("balance.ton.warning")}
      </div>

      <Step n={1} title={t("balance.ton.step1")}>
        <p className="text-sm text-slate-600">{t("balance.ton.step1.desc")}</p>
        <MemoForm currentMemo={memo} />
      </Step>

      <Step n={2} title={t("balance.ton.step2")}>
        <CopyCard
          label={t("balance.ton.copy_label")}
          value={walletAddress}
          note={t("balance.ton.copy_note")}
        />
        {!memo && (
          <Notice variant="warn">{t("balance.ton.no_memo_notice")}</Notice>
        )}
      </Step>
    </section>
  );
}

function MemoForm({ currentMemo }: { currentMemo: string | null }) {
  const memoQ = useDepositMemo();
  const generate = useGenerateDepositMemo();
  const totp = useTotpStatus();
  const push = useToasts((s) => s.push);
  const t = useT();

  // Prefer the live `useDepositMemo` cache (rotates when the user
  // generates) and fall back to whatever payment_destinations gave us
  // on first load.
  const stored = memoQ.data?.memo ?? currentMemo ?? "";

  const [confirming, setConfirming] = useState(false);
  const [pwd, setPwd] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(handle);
  }, [copied]);

  const copy = async () => {
    if (!stored) return;
    try {
      await navigator.clipboard.writeText(stored);
      setCopied(true);
    } catch {
      push({ kind: "error", title: t("balance.copy_failed") });
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pwd) return;

    const askCode =
      !!totp.data?.enabled && !!totp.data?.require_deposit_change;
    let codeError: string | undefined;
    for (;;) {
      let code: string | undefined;
      if (askCode) {
        const got = await openTotpPrompt({
          title: stored
            ? t("balance.ton.confirm.rotate.title")
            : t("balance.ton.confirm.create.title"),
          description: stored
            ? t("balance.ton.confirm.rotate.desc")
            : t("balance.ton.confirm.create.desc"),
          errorMessage: codeError,
        });
        if (got === null) return;
        code = got;
      }
      try {
        await generate.mutateAsync({
          current_password: pwd,
          ...(code ? { code } : {}),
        });
        push({
          kind: "success",
          title: stored ? t("balance.ton.rotated") : t("balance.ton.generated"),
        });
        setConfirming(false);
        setPwd("");
        return;
      } catch (err) {
        const e = err as { status?: unknown; detail?: unknown };
        if (
          e.status === 428 &&
          typeof e.detail === "object" &&
          e.detail !== null &&
          (e.detail as { requires_totp?: unknown }).requires_totp === true
        ) {
          codeError = t("balance.wrong_code");
          continue;
        }
        const msg =
          typeof e.detail === "string"
            ? e.detail
            : t("balance.ton.could_not_generate");
        push({ kind: "error", title: t("balance.ton.generation_failed"), body: msg });
        return;
      }
    }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-surface-border bg-slate-50 px-3 py-2">
        <div className="font-mono text-base font-semibold text-slate-800">
          {stored || <span className="text-slate-400">{t("balance.ton.not_generated")}</span>}
        </div>
        <div className="flex gap-2">
          {stored && !confirming && (
            <button
              type="button"
              onClick={copy}
              className={`btn text-xs ${copied ? "btn-primary" : "btn-ghost"}`}
            >
              {copied ? t("balance.ton.copied") : t("balance.ton.copy")}
            </button>
          )}
          {!confirming && (
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => {
                setPwd("");
                setConfirming(true);
              }}
            >
              {stored ? t("balance.ton.generate_new") : t("balance.ton.generate")}
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <form
          onSubmit={submit}
          className="mt-3 grid gap-3 sm:max-w-md"
        >
          {stored && (
            <Notice variant="warn">{t("balance.ton.rotate_warning")}</Notice>
          )}
          <div>
            <label className="label" htmlFor="ton_pwd">
              {t("balance.password.current")}
            </label>
            <input
              id="ton_pwd"
              type="password"
              autoComplete="current-password"
              className="input"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => {
                setConfirming(false);
                setPwd("");
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary text-xs"
              disabled={generate.isPending || !pwd}
            >
              {generate.isPending
                ? t("balance.ton.generating")
                : stored
                  ? t("balance.ton.rotate_btn")
                  : t("balance.ton.generate_btn")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ---------------------------- Primitives ---------------------------- */

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-5 border-t border-surface-border pt-4">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
          {n}
        </span>
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </div>
      <div className="mt-2 pl-8">{children}</div>
    </div>
  );
}

function CopyCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: ReactNode;
}) {
  const push = useToasts((s) => s.push);
  const t = useT();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(handle);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      push({ kind: "error", title: t("balance.copy_failed") });
    }
  };

  return (
    <div className="mt-3 rounded-md border border-accent bg-accent-soft p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-accent">
        {label}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div className="break-all font-mono text-base font-semibold text-slate-800">
          {value}
        </div>
        <button
          type="button"
          onClick={copy}
          className={`btn text-xs ${copied ? "btn-primary" : "btn-ghost"}`}
        >
          {copied ? t("balance.ton.copied") : t("balance.ton.copy")}
        </button>
      </div>
      {note && <div className="mt-2 text-xs text-slate-600">{note}</div>}
    </div>
  );
}

function Notice({
  children,
  variant = "info",
}: {
  children: ReactNode;
  variant?: "info" | "warn";
}) {
  const cls =
    variant === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${cls}`}>
      {children}
    </div>
  );
}

// Same formatter as the layout header so the two never disagree.
function formatBalance(
  liveStr: string | undefined,
  meValue: number | undefined,
): string {
  if (liveStr !== undefined) {
    const n = Number(liveStr);
    if (Number.isFinite(n)) return n.toFixed(4);
    return liveStr;
  }
  if (meValue !== undefined) return meValue.toFixed(4);
  return "-";
}
